import React, { useEffect, useMemo, useState } from "react";
import { formatShortDate } from "./financial/financialUtils";
import FinancialKpis from "./financial/FinancialKpis";
import CobranzaChartCard from "./financial/CobranzaChartCard";
import MetodosChartCard from "./financial/MetodosChartCard";
import FacturasTable from "./financial/FacturasTable";

const buildDefaultFilters = (base = {}) => ({
  centro_id: base.centro_id || "",
  curso_id: base.curso_id || "",
  promocion_id: base.promocion_id || "",
  cobranzaYear: "all",
  cobranzaMonth: "all",
  metodoYear: "all",
  metodoMonth: "all",
});

export default function FinancialTab({
  financiero = {},
  centros = [],
  cursos = [],
  promociones = [],
  globalFilters = {},
  showKpis = true,
}) {
  const kpis = financiero.kpis || [];
  const facturas = financiero.facturas || [];
  const metodos = useMemo(() => financiero.metodoPago || [], [financiero.metodoPago]);
  const cobranzaDia = useMemo(() => financiero.cobranzaDia || [], [financiero.cobranzaDia]);
  const cobranzaMonthly = useMemo(() => financiero.cobranzaMonthly || [], [financiero.cobranzaMonthly]);
  const metodoPagoMonthly = useMemo(() => financiero.metodoPagoMonthly || [], [financiero.metodoPagoMonthly]);
  const [filters, setFilters] = useState(buildDefaultFilters(globalFilters));
  const [draftFilters, setDraftFilters] = useState(buildDefaultFilters(globalFilters));
  const [openDropdown, setOpenDropdown] = useState(null);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      centro_id: globalFilters.centro_id || "",
      curso_id: globalFilters.curso_id || "",
      promocion_id: globalFilters.promocion_id || "",
    }));
    setDraftFilters((prev) => ({
      ...prev,
      centro_id: globalFilters.centro_id || "",
      curso_id: globalFilters.curso_id || "",
      promocion_id: globalFilters.promocion_id || "",
    }));
  }, [globalFilters.centro_id, globalFilters.curso_id, globalFilters.promocion_id]);

  const toggleDropdown = (section) => {
    setDraftFilters(filters);
    setOpenDropdown((prev) => (prev === section ? null : section));
  };

  const closeDropdown = () => {
    setOpenDropdown(null);
  };

  const applyFilters = () => {
    setFilters(draftFilters);
    closeDropdown();
  };

  const resetDraftFilters = () => {
    setDraftFilters(buildDefaultFilters());
  };

  const cobranzaPeriod = useMemo(
    () =>
      filters.cobranzaYear !== "all" && filters.cobranzaMonth !== "all"
        ? `${filters.cobranzaYear}-${filters.cobranzaMonth}`
        : null,
    [filters.cobranzaYear, filters.cobranzaMonth]
  );

  const cobranzaSource = useMemo(() => {
    if (cobranzaPeriod) {
      return (cobranzaMonthly || []).filter((row) => row.period === cobranzaPeriod);
    }
    if (filters.cobranzaYear !== "all") {
      const year = String(filters.cobranzaYear);
      return (cobranzaMonthly || []).filter((row) => (row.period || "").startsWith(`${year}-`));
    }
    return cobranzaDia || [];
  }, [cobranzaPeriod, filters.cobranzaYear, cobranzaMonthly, cobranzaDia]);

  const metodoPeriod = useMemo(
    () =>
      filters.metodoYear !== "all" && filters.metodoMonth !== "all"
        ? `${filters.metodoYear}-${filters.metodoMonth}`
        : null,
    [filters.metodoYear, filters.metodoMonth]
  );

  const metodosSource = useMemo(() => {
    if (metodoPeriod) {
      return (metodoPagoMonthly || []).filter((row) => row.period === metodoPeriod);
    }
    if (filters.metodoYear !== "all") {
      const year = String(filters.metodoYear);
      return (metodoPagoMonthly || []).filter((row) => (row.period || "").startsWith(`${year}-`));
    }
    return metodos || [];
  }, [metodoPeriod, filters.metodoYear, metodoPagoMonthly, metodos]);

  const pieTotal = useMemo(() => {
    const matchIds = (row) => {
      const matchCentro = !filters.centro_id || String(row.centro_id || "") === String(filters.centro_id);
      const matchCurso = !filters.curso_id || String(row.curso_id || "") === String(filters.curso_id);
      const matchPromo = !filters.promocion_id || String(row.promocion_id || "") === String(filters.promocion_id);
      return matchCentro && matchCurso && matchPromo;
    };
    return metodosSource.filter(matchIds).reduce((acc, m) => acc + Number(m.value || m.total || 0), 0);
  }, [metodosSource, filters.centro_id, filters.curso_id, filters.promocion_id]);

  const pieData = useMemo(() => {
    const matchIds = (row) => {
      const matchCentro = !filters.centro_id || String(row.centro_id || "") === String(filters.centro_id);
      const matchCurso = !filters.curso_id || String(row.curso_id || "") === String(filters.curso_id);
      const matchPromo = !filters.promocion_id || String(row.promocion_id || "") === String(filters.promocion_id);
      return matchCentro && matchCurso && matchPromo;
    };
    if (!pieTotal) return [];
    const map = new Map();
    metodosSource.filter(matchIds).forEach((m) => {
      const key = m.metodo || m.metodo_pago || "Sin metodo";
      const prev = map.get(key) || 0;
      map.set(key, prev + Number(m.value || m.total || 0));
    });
    return Array.from(map.entries()).map(([metodo, value]) => ({ metodo, value }));
  }, [metodosSource, pieTotal, filters.centro_id, filters.curso_id, filters.promocion_id]);

  const barData = useMemo(() => {
    const matchIds = (row) => {
      const matchCentro = !filters.centro_id || String(row.centro_id || "") === String(filters.centro_id);
      const matchCurso = !filters.curso_id || String(row.curso_id || "") === String(filters.curso_id);
      const matchPromo = !filters.promocion_id || String(row.promocion_id || "") === String(filters.promocion_id);
      return matchCentro && matchCurso && matchPromo;
    };
    const map = new Map();
    (cobranzaSource || []).filter(matchIds).forEach((c) => {
      const key = c.dia;
      const prev = map.get(key) || 0;
      map.set(key, prev + Number(c.total || 0));
    });
    const aggregated = Array.from(map.entries())
      .map(([dia, total]) => ({ dia, total }))
      .sort((a, b) => new Date(a.dia) - new Date(b.dia));

    return aggregated.map((c, idx) => ({
      name: formatShortDate(c.dia, idx),
      cobrado: Number(c.total || 0),
      fechaLabel: c.dia ? new Date(c.dia).toLocaleDateString("es-EC") : "",
    }));
  }, [cobranzaSource, filters.centro_id, filters.curso_id, filters.promocion_id]);

  const cobranzaYears = Array.from(
    new Set(
      cobranzaMonthly
        .map((row) => (row.period ? row.period.slice(0, 4) : null))
        .filter(Boolean)
    )
  ).sort((a, b) => b.localeCompare(a));
  const cobranzaMonths = Array.from(
    new Set(
      cobranzaMonthly
        .filter((row) =>
          draftFilters.cobranzaYear === "all"
            ? Boolean(row.period)
            : row.period?.startsWith(`${draftFilters.cobranzaYear}-`)
        )
        .map((row) => (row.period ? row.period.slice(5, 7) : null))
        .filter(Boolean)
    )
  ).sort();

  const metodoYears = Array.from(
    new Set(
      metodoPagoMonthly
        .map((row) => (row.period ? row.period.slice(0, 4) : null))
        .filter(Boolean)
    )
  ).sort((a, b) => b.localeCompare(a));
  const metodoMonths = Array.from(
    new Set(
      metodoPagoMonthly
        .filter((row) =>
          draftFilters.metodoYear === "all"
            ? Boolean(row.period)
            : row.period?.startsWith(`${draftFilters.metodoYear}-`)
        )
        .map((row) => (row.period ? row.period.slice(5, 7) : null))
        .filter(Boolean)
    )
  ).sort();

  const centroOptions = useMemo(() => centros || [], [centros]);
  const cursoOptions = useMemo(() => cursos || [], [cursos]);
  const derivedPromociones = useMemo(() => {
    const map = new Map();
    const collect = (row) => {
      const id = row?.promocion_id;
      if (!id) return;
      if (!map.has(id)) {
        map.set(id, {
          id,
          numero_promocion: row?.promocion || row?.numero_promocion || `Promo ${id}`,
          curso_id: row?.curso_id || null,
        });
      }
    };
    (cobranzaDia || []).forEach(collect);
    (cobranzaMonthly || []).forEach(collect);
    (metodos || []).forEach(collect);
    (metodoPagoMonthly || []).forEach(collect);
    return Array.from(map.values());
  }, [cobranzaDia, cobranzaMonthly, metodos, metodoPagoMonthly]);
  const promoOptions = useMemo(() => {
    if (Array.isArray(promociones) && promociones.length) return promociones;
    return derivedPromociones;
  }, [promociones, derivedPromociones]);
  const promoOptionsFiltered = useMemo(() => {
    if (!draftFilters.curso_id) return promoOptions;
    return promoOptions.filter(
      (p) => String(p.curso_id || "") === String(draftFilters.curso_id)
    );
  }, [promoOptions, draftFilters.curso_id]);

  return (
    <>
      {showKpis && <FinancialKpis kpis={kpis} />}

      <div className={`row g-3 ${showKpis ? "mt-3" : ""}`}>
        <div className="col-12 col-xl-6">
          <CobranzaChartCard
            barData={barData}
            open={openDropdown === "cobranza"}
            onToggle={() => toggleDropdown("cobranza")}
            onClose={closeDropdown}
            draftFilters={draftFilters}
            setDraftFilters={setDraftFilters}
            centroOptions={centroOptions}
            cursoOptions={cursoOptions}
            promoOptions={promoOptionsFiltered}
            cobranzaYears={cobranzaYears}
            cobranzaMonths={cobranzaMonths}
            onResetFilters={resetDraftFilters}
            onApplyFilters={applyFilters}
          />
        </div>

        <div className="col-12 col-xl-6">
          <MetodosChartCard
            pieData={pieData}
            pieTotal={pieTotal}
            open={openDropdown === "metodos"}
            onToggle={() => toggleDropdown("metodos")}
            onClose={closeDropdown}
            draftFilters={draftFilters}
            setDraftFilters={setDraftFilters}
            centroOptions={centroOptions}
            cursoOptions={cursoOptions}
            promoOptions={promoOptionsFiltered}
            metodoYears={metodoYears}
            metodoMonths={metodoMonths}
            onResetFilters={resetDraftFilters}
            onApplyFilters={applyFilters}
          />
        </div>
      </div>

      <FacturasTable facturas={facturas} />
    </>
  );
}
